import { Link } from 'react-router-dom';
import type { VideoSummary } from '@streamr/shared';
import { Avatar } from './ui';
import { cn, formatCount, formatDuration, formatRelativeTime } from '../lib/format';

/**
 * One card, five surfaces (home, search, channel, up-next, studio). The shape
 * it renders is `videoSummarySchema` exactly -- if the feed and search
 * responses ever disagree by a field, that is a type error here rather than an
 * undefined in a grid.
 */

const STATUS_LABEL: Record<VideoSummary['status'], string | null> = {
  UPLOADING: 'Uploading',
  PROCESSING: 'Processing',
  READY: null,
  FAILED: 'Failed',
};

const VISIBILITY_LABEL: Record<VideoSummary['visibility'], string | null> = {
  PUBLIC: null,
  UNLISTED: 'Unlisted',
  PRIVATE: 'Private',
};

export const VideoCard = ({
  video,
  compact = false,
  showChannel = true,
}: {
  video: VideoSummary;
  compact?: boolean;
  showChannel?: boolean;
}): JSX.Element => {
  const statusLabel = STATUS_LABEL[video.status];
  const visibilityLabel = VISIBILITY_LABEL[video.visibility];
  /*
   * A video that is not READY has no manifest to play. It is deliberately not a
   * link: navigating to a watch page whose only honest content is an error is
   * worse than a card that does not respond.
   */
  const playable = video.status === 'READY';
  const watchPath = `/watch/${video.id}`;

  const thumbnail = (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl bg-ink-700',
        compact ? 'aspect-video w-40 shrink-0' : 'aspect-video w-full',
      )}
    >
      {video.posterUrl !== null && video.posterUrl !== '' ? (
        <img src={video.posterUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-ink-400">
          <span className="text-xs">{statusLabel ?? 'No preview'}</span>
        </div>
      )}
      {playable && video.durationSec !== null && (
        <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1.5 py-0.5 text-xs font-medium text-white">
          {formatDuration(video.durationSec)}
        </span>
      )}
      {statusLabel !== null && (
        <span className="absolute left-1 top-1 rounded bg-black/80 px-1.5 py-0.5 text-xs text-amber-300">
          {statusLabel}
        </span>
      )}
    </div>
  );

  /*
   * The thumbnail and the title are separate links to the same place, and the
   * channel name is a third link to somewhere else. Wrapping the whole card in
   * one anchor would nest the channel link inside it -- invalid HTML that
   * browsers recover from unpredictably and that leaves screen readers
   * announcing one link containing another.
   *
   * The thumbnail is removed from the tab order because it duplicates the
   * title's destination; a keyboard user gets one stop per destination.
   */
  const thumbnailArea = playable ? (
    <Link to={watchPath} tabIndex={-1} aria-hidden="true" className="block">
      {thumbnail}
    </Link>
  ) : (
    thumbnail
  );

  const titleClass = cn('line-clamp-2 font-medium text-ink-100', compact ? 'text-sm' : 'text-base');

  const meta = (
    <div className="min-w-0 flex-1">
      <h3 className={compact ? 'text-sm' : 'text-base'}>
        {playable ? (
          <Link
            to={watchPath}
            className={cn(titleClass, 'rounded outline-none hover:text-white focus-visible:ring-2 focus-visible:ring-brand-400')}
          >
            {video.title}
          </Link>
        ) : (
          <span className={titleClass}>{video.title}</span>
        )}
      </h3>
      {showChannel && (
        <Link to={`/@${video.channel.handle}`} className="mt-1 block truncate text-sm text-ink-300 hover:text-ink-100">
          {video.channel.displayName}
        </Link>
      )}
      <p className="mt-0.5 text-xs text-ink-400">
        {formatCount(video.viewCount)} views
        {video.publishedAt !== null && <> &middot; {formatRelativeTime(video.publishedAt)}</>}
        {visibilityLabel !== null && <> &middot; {visibilityLabel}</>}
      </p>
    </div>
  );

  return (
    <article className={playable ? undefined : 'opacity-70'}>
      {compact ? (
        <div className="flex gap-3">
          {thumbnailArea}
          {meta}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {thumbnailArea}
          <div className="flex gap-3">
            {showChannel && <Avatar src={video.channel.avatarUrl} name={video.channel.displayName} size={36} />}
            {meta}
          </div>
        </div>
      )}
    </article>
  );
};
